import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { propTypes } from '../../util/types';
import { ensureUser, userDisplayNameAsString } from '../../util/data';
import { withViewport } from '../../util/contextHelpers';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  SectionRecentlyViewedListings,
} from '../../components';
import { TopbarContainer, NotFoundPage } from '../../containers';

// External
import config from '../../config';

// Shared
import AsideContent from './AsideContent';
import SectionInfo from './SectionInfo';
import SectionDetails from './SectionDetails';
import SectionReviews from './SectionReviews';
import SectionSubscriptions from './SectionSubscriptions';
import SectionFollowingUsers from './SectionFollowingUsers';
import SectionUserActivity from './SectionUserActivity';
import css from './ProfilePage.module.css';

const isBetaMode = process.env.REACT_APP_BETA_MODE === 'true';

const ProfilePageComponent = props => {
  const {
    scrollingDisabled,
    user,
    filterConfig,
    userShowError,
    userReviews,
    queryUserReviewsInProgress,
    queryUserReviewsError,
    userComments,
    recentlyViewedListings,
    queryRecentlyViewedListingsInProgress,
    queryRecentlyViewedListingsError,
    subscriptionListings,
    fetchSubscriptionListingsInProgress,
    fetchSubscriptionListingsError,
    followingUsers,
    fetchFollowingUsersInProgress,
    fetchFollowingUsersError,
    communityPosts,
    activityComments,
    fetchUserActivityInProgress,
    fetchUserActivityError,
    currentUser,
    intl,
  } = props;

  const profileUser = ensureUser(user);
  const displayName = userDisplayNameAsString(profileUser);

  const asideContent = (
    <AsideContent user={profileUser} userShowError={userShowError} displayName={displayName} />
  );

  const showFollowingUserSection = false;

  const mainContent = (
    <div className={css.mainContent}>
      <SectionInfo
        intl={intl}
        user={profileUser}
        currentUser={currentUser}
        userReviews={userReviews}
        userComments={userComments}
        isBetaMode={isBetaMode}
      />
      <SectionDetails
        intl={intl}
        filterConfig={filterConfig}
        profileUser={profileUser}
        isBetaMode={isBetaMode}
      />
      <SectionUserActivity
        posts={communityPosts}
        comments={activityComments}
        inProgress={fetchUserActivityInProgress}
        error={fetchUserActivityError}
      />
      {!isBetaMode && (
        <SectionReviews
          intl={intl}
          userReviews={userReviews}
          queryUserReviewsInProgress={queryUserReviewsInProgress}
          queryUserReviewsError={queryUserReviewsError}
        />
      )}
      {!isBetaMode && (
        <SectionSubscriptions
          intl={intl}
          subscriptionListings={subscriptionListings}
          fetchSubscriptionListingsInProgress={fetchSubscriptionListingsInProgress}
          fetchSubscriptionListingsError={fetchSubscriptionListingsError}
        />
      )}
      {showFollowingUserSection ? (
        <SectionFollowingUsers
          intl={intl}
          followingUsers={followingUsers}
          fetchFollowingUsersInProgress={fetchFollowingUsersInProgress}
          fetchFollowingUsersError={fetchFollowingUsersError}
        />
      ) : null}
    </div>
  );

  let content;

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage />;
  } else if (userShowError) {
    content = (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  } else {
    content = mainContent;
  }

  const schemaTitle = intl.formatMessage(
    {
      id: 'ProfilePage.schemaTitle',
    },
    {
      name: displayName,
      siteTitle: config.siteTitle,
    }
  );

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      currentPage="ProfilePage"
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer
            currentPage="ProfilePage"
            desktopClassName={css.desktopTopbar}
            contentClassName={css.topbarContent}
          />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.content}>
            {asideContent}
            {content}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          {!isBetaMode && (
            <SectionRecentlyViewedListings
              intl={intl}
              recentlyViewedListings={recentlyViewedListings}
              queryRecentlyViewedListingsInProgress={queryRecentlyViewedListingsInProgress}
              queryRecentlyViewedListingsError={queryRecentlyViewedListingsError}
            />
          )}
          <Footer currentPage="ProfilePage" />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  filterConfig: config.custom.filters,

  // reviews
  userReviews: [],
  queryUserReviewsInProgress: false,
  queryUserReviewsError: null,

  // comments
  userComments: [],
  queryUserCommentsInProgress: false,
  queryUserCommentsError: null,

  // experience(s)
  recentlyViewedListings: [],
  queryRecentlyViewedListingsInProgress: false,
  queryRecentlyViewedListingsError: null,

  // subscription listings
  fetchSubscriptionListingsInProgress: false,
  fetchSubscriptionListingsError: null,
  subscriptionListings: [],

  // users
  fetchFollowingUsersInProgress: false,
  fetchFollowingUsersError: null,
  followingUsers: [],
};

const { bool, number, arrayOf, shape } = PropTypes;

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,

  // reviews
  userReviews: arrayOf(propTypes.review).isRequired,
  queryUserReviewsInProgress: bool.isRequired,
  queryUserReviewsError: propTypes.error,

  // comments
  userComments: arrayOf(propTypes.comment).isRequired,
  queryUserCommentsInProgress: bool.isRequired,
  queryUserCommentsError: propTypes.error,

  // experience(s)
  recentlyViewedListings: arrayOf(propTypes.listing).isRequired,
  queryRecentlyViewedListingsInProgress: bool.isRequired,
  queryRecentlyViewedListingsError: propTypes.error,

  // subscription listings
  fetchSubscriptionListingsInProgress: bool.isRequired,
  fetchSubscriptionListingsError: propTypes.error,
  subscriptionListings: arrayOf(propTypes.listing).isRequired,

  // users
  fetchFollowingUsersInProgress: bool.isRequired,
  fetchFollowingUsersError: propTypes.error,
  followingUsers: arrayOf(propTypes.user).isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    currentUser,
    recentlyViewedListingIds,
    queryRecentlyViewedListingsInProgress,
    queryRecentlyViewedListingsError,
  } = state.user;
  const {
    userId,
    userShowError,
    userReviews,
    queryUserReviewsInProgress,
    queryUserReviewsError,
    userComments,
    queryUserCommentsInProgress,
    queryUserCommentsError,
    subscriptionListingIds,
    fetchSubscriptionListingsInProgress,
    fetchSubscriptionListingsError,
    followingUserRefs,
    fetchFollowingUsersInProgress,
    fetchFollowingUsersError,
    communityPosts,
    activityComments,
    fetchUserActivityInProgress,
    fetchUserActivityError,
  } = state.ProfilePage;

  // user
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // users
  const followingUsers = getMarketplaceEntities(state, followingUserRefs);

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    recentlyViewedListings: getListingsById(state, recentlyViewedListingIds),
    queryRecentlyViewedListingsInProgress,
    queryRecentlyViewedListingsError,
    userReviews,
    queryUserReviewsInProgress,
    queryUserReviewsError,
    userComments,
    queryUserCommentsInProgress,
    queryUserCommentsError,
    subscriptionListings: getListingsById(state, subscriptionListingIds),
    fetchSubscriptionListingsInProgress,
    fetchSubscriptionListingsError,
    followingUsers,
    fetchFollowingUsersInProgress,
    fetchFollowingUsersError,
    communityPosts,
    activityComments,
    fetchUserActivityInProgress,
    fetchUserActivityError,
  };
};

const ProfilePage = compose(
  connect(mapStateToProps),
  withViewport,
  injectIntl
)(ProfilePageComponent);

export default ProfilePage;
